import { spacingPropToCSS } from '@nect/ui/theme/helper';
import styled, { css } from 'styled-components';

import { IHeadlineProps } from '.';

type IHeadlinePropsStyled = Omit<IHeadlineProps, 'children'>;

const HeadlineCSS = css<IHeadlinePropsStyled>`
    font-family: ${(props) => props.theme.heading.fontFamily};
    font-style: normal;
    font-weight: bold;
    color: ${(props) =>
        props.color
            ? props.theme.color[props.color]
            : props.theme.heading.color.default};
    text-align: ${(props) => props.align ?? 'left'};
    margin: 0;
    ${({ margin, theme }) =>
        margin && spacingPropToCSS(margin, 'margin', theme)}
`;

// H1
export const HeadlineH1CSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 64px;
    line-height: 86px;
    letter-spacing: -3px;
`;

const HeadlineH1Styled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineH1CSS}
`;

// H2
export const HeadlineH2CSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 56px;
    line-height: 64px;
    letter-spacing: -3px;
`;

const HeadlineH2Styled = styled.h2<IHeadlinePropsStyled>`
    ${HeadlineH2CSS}
`;

// H3 Bold
export const HeadlineH3BoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 36px;
    line-height: 48px;
    letter-spacing: -2px;
`;

const HeadlineH3BoldStyled = styled.h3<IHeadlinePropsStyled>`
    ${HeadlineH3BoldCSS}
`;

// H3 Regular
export const HeadlineH3RegularCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 36px;
    line-height: 48px;
    letter-spacing: -2px;
`;

const HeadlineH3RegularStyled = styled.h3<IHeadlinePropsStyled>`
    ${HeadlineH3RegularCSS}
`;

// H4 Bold
export const HeadlineH4BoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 600;
    font-size: 24px;
    line-height: 34px;
    letter-spacing: -1px;
`;

const HeadlineH4BoldStyled = styled.h4<IHeadlinePropsStyled>`
    ${HeadlineH4BoldCSS}
`;

// H4 Regular
export const HeadlineH4RegularCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 24px;
    line-height: 34px;
    letter-spacing: -1px;
`;

const HeadlineH4RegularStyled = styled.h4<IHeadlinePropsStyled>`
    ${HeadlineH4RegularCSS}
`;

// H5 Bold
export const HeadlineH5BoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 600;
    font-size: 21px;
    line-height: 30px;
    letter-spacing: -1px;
`;

const HeadlineH5BoldStyled = styled.h5<IHeadlinePropsStyled>`
    ${HeadlineH5BoldCSS}
`;

// H5 Regular
export const HeadlineH5RegularCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 21px;
    line-height: 30px;
    letter-spacing: -1px;
`;

const HeadlineH5RegularStyled = styled.h5<IHeadlinePropsStyled>`
    ${HeadlineH5RegularCSS}
`;

// H6 Bold
export const HeadlineH6BoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.045em;
`;

const HeadlineH6BoldStyled = styled.h6<IHeadlinePropsStyled>`
    ${HeadlineH6BoldCSS}
`;

// H6 Regular
export const HeadlineH6RegularCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.5px;
`;

const HeadlineH6RegularStyled = styled.h6<IHeadlinePropsStyled>`
    ${HeadlineH6RegularCSS}
`;

// Hero Bold
export const HeadlineHeroBoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 600;
    font-size: 72px;
    line-height: 96px;

    letter-spacing: -4px;
`;

const HeadlineHeroBoldStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineHeroBoldCSS}
`;

// Hero Medium
export const HeadlineHeroMediumCSS = css<IHeadlinePropsStyled>`
    ${HeadlineHeroBoldCSS}
    font-weight: 500;
`;

const HeadlineHeroMediumStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineHeroMediumCSS}
`;

// Hero Light
export const HeadlineHeroLightCSS = css<IHeadlinePropsStyled>`
    ${HeadlineHeroBoldCSS}
    font-weight: 300;
`;

const HeadlineHeroLightStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineHeroLightCSS}
`;

// Colossal Bold
export const HeadlineColossalBoldCSS = css<IHeadlinePropsStyled>`
    ${HeadlineCSS}
    font-weight: 700;
    font-size: 96px;
    line-height: 118px;

    letter-spacing: -4px;
`;

const HeadlineColossalBoldStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineColossalBoldCSS}
`;

// Colossal Medium
export const HeadlineColossalMediumCSS = css<IHeadlinePropsStyled>`
    ${HeadlineColossalBoldCSS}
    font-weight: 500;
`;

const HeadlineColossalMediumStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineColossalMediumCSS}
`;

// Colossal Light
export const HeadlineColossalLightCSS = css<IHeadlinePropsStyled>`
    ${HeadlineColossalBoldCSS}
    font-weight: 275;
`;

const HeadlineColossalLightStyled = styled.h1<IHeadlinePropsStyled>`
    ${HeadlineColossalLightCSS}
`;

// eslint-disable-next-line import/no-default-export
export default {
    HeadlineH1Styled,
    HeadlineH2Styled,

    HeadlineH3BoldStyled,
    HeadlineH3RegularStyled,

    HeadlineH4BoldStyled,
    HeadlineH4RegularStyled,

    HeadlineH5BoldStyled,
    HeadlineH5RegularStyled,

    HeadlineH6BoldStyled,
    HeadlineH6RegularStyled,

    HeadlineHeroBoldStyled,
    HeadlineHeroMediumStyled,
    HeadlineHeroLightStyled,

    HeadlineColossalBoldStyled,
    HeadlineColossalMediumStyled,
    HeadlineColossalLightStyled,
};
